import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Box } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import RewardsCard from '../components/RewardsCard.tsx';
import './Wallet.css';

const Wallet = () => {
  const { connected, publicKey } = useWallet();
  const location = useLocation();
  const [walletData, setWalletData] = useState(null);
  // Set initial tab to 'rewards' if coming from rewards page
  const [activeTab, setActiveTab] = useState(
    location.pathname === '/rewards' ? 'rewards' : 'activity'
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!connected || !publicKey) {
      setIsLoading(false);
      return;
    }

    // This would be replaced with an actual API call
    const fetchWalletData = async () => {
      try {
        console.log("Connected wallet:", publicKey.toString());
        // Simulated API response
        const data = {
          address: publicKey.toString(),
          balance: {
            sol: 2.45,
            desol: 25000
          },
          activity: [
            {
              id: '1',
              type: 'vote',
              description: 'Voted YES on "Buy BONK on recent dip"',
              timestamp: '2023-09-15 14:32',
              txHash: '5xG...7Uh'
            },
            {
              id: '2',
              type: 'proposal',
              description: 'Created proposal "Sell 50% of TRUMP position"',
              timestamp: '2023-09-14 09:15',
              txHash: '3aB...9Zk'
            },
            {
              id: '3',
              type: 'reward',
              description: 'Received 250 DESOL for profitable trade proposal',
              timestamp: '2023-09-13 16:45',
              txHash: '7cD...2Lm'
            },
            {
              id: '4',
              type: 'vote',
              description: 'Voted NO on "Add PEPE to whitelist"',
              timestamp: '2023-09-12 11:20',
              txHash: '8dF...3Pq'
            },
            {
              id: '5',
              type: 'trade',
              description: 'Executed Fast Trade: Bought 10,000 BONK',
              timestamp: '2023-09-11 10:05',
              txHash: '2hJ...5Rt'
            },
            {
              id: '6',
              type: 'reward',
              description: 'Received 100 DESOL for governance participation',
              timestamp: '2023-09-10 15:30',
              txHash: '9kL...1Mn'
            },
            {
              id: '7',
              type: 'trade',
              description: 'Executed Fast Trade: Sold 5,000 TRUMP at profit',
              timestamp: '2023-09-09 09:45',
              txHash: '4pR...6Ts'
            }
          ],
          proposals: [
            {
              id: '1',
              title: 'Buy BONK on recent dip',
              status: 'completed',
              votes: {
                yes: 78,
                no: 22
              },
              timestamp: '2023-09-10 12:30'
            },
            {
              id: '2',
              title: 'Sell 50% of TRUMP position',
              status: 'active',
              votes: {
                yes: 82,
                no: 18
              },
              timestamp: '2023-09-14 09:15'
            },
            {
              id: '3',
              title: 'Add PEPE to whitelist',
              status: 'rejected',
              votes: {
                yes: 35,
                no: 65
              },
              timestamp: '2023-09-12 08:45'
            },
            {
              id: '4',
              title: 'Implement DCA strategy for SOL',
              status: 'pending',
              votes: {
                yes: 0,
                no: 0
              },
              timestamp: '2023-09-16 11:20'
            }
          ],
          rewards: [
            {
              id: '1',
              amount: 250,
              reason: 'Profitable trade proposal: "Buy BONK at $0.00001"',
              status: 'claimed',
              timestamp: '2023-09-13 16:45'
            },
            {
              id: '2',
              amount: 100,
              reason: 'Community contribution: Governance participation',
              status: 'pending',
              timestamp: '2023-09-16 10:20'
            }
          ]
        };
        
        setWalletData(data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching wallet data:', error);
        setIsLoading(false);
      }
    };

    fetchWalletData();
  }, [connected, publicKey]);

  // Reward claiming is now handled by the RewardsCard component

  if (isLoading) {
    return <div className="loading">Loading wallet data...</div>;
  }

  if (!connected) {
    return (
      <div className="wallet-page">
        <h1 className="page-title">Wallet</h1>
        <div className="connect-wallet-container">
          <p>Please connect your wallet to view your activity and rewards</p>
          <WalletMultiButton />
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-page">
      <h1 className="page-title">Wallet</h1>
      
      {walletData && (
        <div className="wallet-overview">
          <div className="wallet-address">
            <span className="label">Connected Wallet</span>
            <span className="value">{walletData.address.slice(0, 6)}...{walletData.address.slice(-4)}</span>
            <span className="wallet-address-full">{walletData.address}</span>
            <a 
              href={`https://solscan.io/account/${walletData.address}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="tx-link"
            >
              View on Solscan
            </a>
          </div>
          <div className="wallet-balances">
            <div className="balance">
              <span className="label">SOL Balance</span>
              <span className="value">{walletData.balance.sol} SOL</span>
            </div>
            <div className="balance">
              <span className="label">DESOL Balance</span>
              <span className="value">{walletData.balance.desol} DESOL</span>
            </div>
          </div>
        </div>
      )}
      
      <div className="tabs">
        <button 
          className={`tab-btn ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          Activity
        </button>
        <button 
          className={`tab-btn ${activeTab === 'proposals' ? 'active' : ''}`}
          onClick={() => setActiveTab('proposals')}
        >
          My Proposals
        </button>
        <button 
          className={`tab-btn ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => setActiveTab('rewards')}
        >
          Rewards
        </button>
      </div>
      
      {activeTab === 'activity' && (
        <div className="activity-list">
          {!walletData || !walletData.activity || walletData.activity.length === 0 ? (
            <div className="no-data">
              <p>No activity found</p>
            </div>
          ) : (
            walletData.activity.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className={`activity-icon ${activity.type}`}></div>
                <div className="activity-content">
                  <p className="activity-description">{activity.description}</p>
                  <div className="activity-meta">
                    <span className="timestamp">{activity.timestamp}</span>
                    <a 
                      href={`https://solscan.io/tx/${activity.txHash}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="tx-link"
                    >
                      View Transaction
                    </a>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      
      {activeTab === 'proposals' && (
        <div className="proposals-list">
          {!walletData || !walletData.proposals || walletData.proposals.length === 0 ? (
            <div className="no-data">
              <p>You haven't created any proposals yet</p>
            </div>
          ) : (
            walletData.proposals.map((proposal) => (
              <div key={proposal.id} className="proposal-item">
                <div className="proposal-content">
                  <h3>{proposal.title}</h3>
                  <div className="proposal-meta">
                    <span className={`status ${proposal.status}`}>{proposal.status}</span>
                    <span className="timestamp">{proposal.timestamp}</span>
                  </div>
                </div>
                <div className="proposal-votes">
                  <div className="vote-progress">
                    <div className="progress-bar">
                      <div 
                        className="yes-progress" 
                        style={{ width: `${proposal.votes.yes}%` }}
                      ></div>
                      <div 
                        className="no-progress" 
                        style={{ width: `${proposal.votes.no}%` }}
                      ></div>
                    </div>
                    <div className="vote-percentages">
                      <span className="yes-percentage">{proposal.votes.yes}% Yes</span>
                      <span className="no-percentage">{proposal.votes.no}% No</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      
      {activeTab === 'rewards' && (
        <Box className="rewards-container" mt={4}>
          <RewardsCard />
        </Box>
      )}
    </div>
  );
};

export default Wallet;